import { ReactNode } from 'react';
import { XCircle, ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@bliks-io/ui';

interface AuthResultStateProps {
  title?: string;
  description: string;
  /** Extra content between the message and the back-to-login action. */
  children?: ReactNode;
  onBackToLogin: () => void;
}

/**
 * Terminal error state for authentication flows: icon in a tinted circle,
 * message, and a back-to-login action. Error-only since the legacy
 * activation/recovery success screens were removed with password auth.
 *
 * @example
 * ```tsx
 * if (failed) {
 *   return (
 *     <AuthResultState
 *       title={t('ssoSignInFailed')}
 *       description={errorMessage}
 *       onBackToLogin={() => navigate({ to: '/login' })}
 *     />
 *   );
 * }
 * ```
 */
export function AuthResultState({
  title,
  description,
  children,
  onBackToLogin
}: AuthResultStateProps) {
  const { t } = useTranslation();

  const descriptionText = <p className="text-sm text-muted-foreground">{description}</p>;

  return (
    <div className="flex flex-col items-center w-full gap-6 text-center">
      <div className="flex items-center justify-center w-16 h-16 rounded-full bg-destructive/10">
        <XCircle className="w-8 h-8 text-destructive" />
      </div>

      {title ? (
        <div className="flex flex-col gap-2">
          <h2 className="text-2xl font-semibold">{title}</h2>
          {descriptionText}
        </div>
      ) : (
        descriptionText
      )}

      {children}

      <Button type="button" onClick={onBackToLogin} className="w-full">
        <ArrowLeft className="w-4 h-4 mr-2" />
        {t('backToLogin')}
      </Button>
    </div>
  );
}
